import { z } from "zod";

export const uuid = z.string().uuid();
export const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "expected YYYY-MM-DD");

// ---------------------------------------------------------------------------
// location
// ---------------------------------------------------------------------------
export const locationKindSchema = z.enum(["bed", "container"]);

// Position is loosely jsonb in the DB but the app owns one shape:
// { x: number, y: number } in viewport-local coordinates.
// See ADR-0009.
export const positionSchema = z.object({
  x: z.number().finite(),
  y: z.number().finite(),
});

export const locationCreateSchema = z.object({
  garden_id: uuid,
  name: z.string().min(1).max(200),
  kind: locationKindSchema,
  polygon: z.unknown().nullable().optional(),
  point: positionSchema.nullable().optional(),
  container_attrs: z.unknown().nullable().optional(),
  polygon_source_geojson: z.unknown().nullable().optional(),
  sun_exposure: z.string().max(200).nullable().optional(),
  soil_notes: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
});
export type LocationCreateInput = z.infer<typeof locationCreateSchema>;

export const locationUpdateSchema = locationCreateSchema.partial();
export type LocationUpdateInput = z.infer<typeof locationUpdateSchema>;

// ---------------------------------------------------------------------------
// plant
// ---------------------------------------------------------------------------
export const plantSymbolSchema = z.enum([
  // trees
  "tree", "tree_conifer", "tree_fruit",
  // shrubs
  "shrub", "shrub_topiary", "bamboo",
  // flowers
  "rose", "daisy", "tulip", "sunflower", "lavender", "foxglove",
  // climbers
  "vine", "climber",
  // edibles
  "veg", "carrot", "tomato", "herb",
  // other
  "fern", "container",
]);

export const plantStatusSchema = z.enum([
  "alive",
  "dead",
  "removed",
  "needs_identification",
]);

export const plantCreateSchema = z.object({
  /** Optional: derived from location_id by the BEFORE INSERT trigger
   *  (migration 0011). Required only when location_id is null. */
  garden_id: uuid.optional(),
  location_id: uuid.nullable().optional(),
  position: positionSchema.nullable().optional(),
  common_name: z.string().min(1).max(200),
  scientific_name: z.string().max(200).nullable().optional(),
  cultivar: z.string().max(200).nullable().optional(),
  quantity: z.number().int().positive().optional(),
  planted_date: isoDate.nullable().optional(),
  source_acquisition: z.string().nullable().optional(),
  status: plantStatusSchema.optional(),
  notes: z.string().nullable().optional(),
  parent_plant_id: uuid.nullable().optional(),
  succession_label: z.string().max(200).nullable().optional(),
  propagation_method: z.string().max(200).nullable().optional(),
  symbol: plantSymbolSchema.nullable().optional(),
});
export type PlantCreateInput = z.infer<typeof plantCreateSchema>;

export const plantUpdateSchema = plantCreateSchema.partial();
export type PlantUpdateInput = z.infer<typeof plantUpdateSchema>;

// ---------------------------------------------------------------------------
// plant_attribute
// ---------------------------------------------------------------------------
export const plantAttributeCreateSchema = z.object({
  plant_id: uuid,
  key: z.string().min(1).max(80),
  value: z.string().max(400).nullable().optional(),
  notes: z.string().max(2000).nullable().optional(),
});
export type PlantAttributeCreateInput = z.infer<typeof plantAttributeCreateSchema>;

export const plantAttributeUpdateSchema = plantAttributeCreateSchema
  .omit({ plant_id: true })
  .partial();
export type PlantAttributeUpdateInput = z.infer<typeof plantAttributeUpdateSchema>;

// ---------------------------------------------------------------------------
// plant_observation  (append-only — no update schema)
// ---------------------------------------------------------------------------
export const observationKindSchema = z.enum([
  "general",
  "yield",
  "health",
  "flowering",
  "pest",
  "weather",
  "move",
  "rotation_note",
]);
export type ObservationKind = z.infer<typeof observationKindSchema>;

export const observationCreateSchema = z.object({
  plant_id: uuid,
  observation_date: isoDate,
  kind: observationKindSchema.nullable().optional(),
  body: z.string().max(20000).nullable().optional(),
  data: z.unknown().nullable().optional(),
  photo_id: uuid.nullable().optional(),
});
export type ObservationCreateInput = z.infer<typeof observationCreateSchema>;

// ---------------------------------------------------------------------------
// task
// ---------------------------------------------------------------------------
export const taskCategorySchema = z.enum([
  "care",
  "growing",
  "project",
  "purchase",
  "maintenance",
]);
export type TaskCategoryInput = z.infer<typeof taskCategorySchema>;

export const taskStatusSchema = z.enum([
  "pending",
  "complete",
  "skipped",
  "cancelled",
]);

export const taskCreateSchema = z.object({
  /** Optional: derived from plant_id or location_id by trigger
   *  (migration 0011). Required when neither is provided. */
  garden_id: uuid.optional(),
  name: z.string().min(1).max(200),
  category: taskCategorySchema,
  plant_id: uuid.nullable().optional(),
  location_id: uuid.nullable().optional(),
  description: z.string().max(20000).nullable().optional(),
  due_date: isoDate.nullable().optional(),
  notes: z.string().max(2000).nullable().optional(),
  status: taskStatusSchema.optional(),
});
export type TaskCreateInput = z.infer<typeof taskCreateSchema>;

export const taskUpdateSchema = taskCreateSchema.partial();
export type TaskUpdateInput = z.infer<typeof taskUpdateSchema>;

// ---------------------------------------------------------------------------
// task_completion
// ---------------------------------------------------------------------------
export const taskCompletionCreateSchema = z.object({
  task_id: uuid,
  completed_date: isoDate.optional(),
  notes: z.string().max(2000).nullable().optional(),
  photo_id: uuid.nullable().optional(),
  is_skip: z.boolean().optional(),
});
export type TaskCompletionCreateInput = z.infer<typeof taskCompletionCreateSchema>;
